import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * Hook for broadcasting and listening to matrix updates
 * Uses Supabase Realtime Broadcast for instant, scalable updates
 *
 * @param {string} projectId - The project/space ID for the matrix
 * @param {function} onUpdate - Callback when matrix update is broadcast
 * @returns {object} { broadcastUpdate } - Function to broadcast updates
 */
export function useMatrixBroadcast(projectId, onUpdate) {
  const channelRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Broadcast a matrix update to all connected clients
  const broadcastUpdate = useCallback(async (metadata = {}) => {
    if (!projectId) return;

    try {
      const channel = supabase.channel(`matrix:${projectId}`);

      await channel.send({
        type: 'broadcast',
        event: 'matrix_updated',
        payload: {
          timestamp: new Date().toISOString(),
          ...metadata
        }
      });

      console.log('📡 Broadcast matrix update for project:', projectId, metadata);
    } catch (error) {
      console.error('Failed to broadcast matrix update:', error);
    }
  }, [projectId]);

  // Subscribe to matrix updates
  useEffect(() => {
    if (!projectId || !onUpdate) return;

    const channelName = `matrix:${projectId}`;
    console.log('🎧 Subscribing to matrix broadcasts:', channelName);

    // Create and subscribe to channel
    const channel = supabase.channel(channelName);

    channel
      .on('broadcast', { event: 'matrix_updated' }, (payload) => {
        console.log('📨 Received matrix update broadcast:', payload);

        // Debounce updates to avoid excessive refreshes
        // If multiple changes happen rapidly, only refresh once
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          onUpdate(payload.payload);
          debounceTimerRef.current = null;
        }, 300); // 300ms debounce
      })
      .subscribe((status) => {
        console.log('📡 Matrix broadcast channel status:', status);
      });

    channelRef.current = channel;

    // Cleanup
    return () => {
      console.log('🔌 Unsubscribing from matrix broadcasts:', channelName);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      channel.unsubscribe();
    };
  }, [projectId, onUpdate]);

  return { broadcastUpdate };
}

/**
 * Standalone function to broadcast matrix update (for use outside hooks)
 *
 * @param {string} projectId - The project/space ID
 * @param {object} metadata - Optional metadata about the change
 */
export async function broadcastMatrixUpdate(projectId, metadata = {}) {
  if (!projectId) return;

  try {
    const channel = supabase.channel(`matrix:${projectId}`);

    await channel.send({
      type: 'broadcast',
      event: 'matrix_updated',
      payload: {
        timestamp: new Date().toISOString(),
        ...metadata
      }
    });

    console.log('📡 Broadcast matrix update for project:', projectId, metadata);

    // Clean up the temporary channel
    setTimeout(() => channel.unsubscribe(), 1000);
  } catch (error) {
    console.error('Failed to broadcast matrix update:', error);
  }
}
